/**
 * Date utility functions for handling dates without timezone issues
 */

/**
 * Format a date for use in HTML date inputs (YYYY-MM-DD) without timezone conversion
 * 
 * This function extracts local date components directly instead of converting to UTC,
 * which prevents timezone-related date shifts that can occur with toISOString().
 * 
 * Example:
 * - Date: January 20, 2026 (user's local time)
 * - toISOString().split('T')[0]: May return "2026-01-19" (UTC conversion issue)
 * - formatDateForInput(): Returns "2026-01-20" (correct local date)
 * 
 * @param date - Date object or date string to format
 * @returns Date string in YYYY-MM-DD format (local timezone)
 */
export function formatDateForInput(date: Date | string): string {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
/**
 * Parse a date string from an HTML date input (YYYY-MM-DD) into a local Date object.
 * 
 * This creates a Date object set to midnight in the user's local timezone,
 * rather than UTC midnight (which is what new Date("YYYY-MM-DD") does).
 * 
 * use this when saving date inputs to Firestore to ensure widespread consistency.
 * 
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object at local midnight
 */
export function parseDateFromInput(dateString: string): Date {
  // Handle empty or invalid strings gracefully if needed, though usually controlled by input type="date"
  if (!dateString) return new Date();
  
  const [year, month, day] = dateString.split('-').map(Number);
  // Set to Noon (12:00) to avoid timezone shifts when users in different US timezones view the date
  // e.g. 00:00 EST is 21:00 PST (previous day), but 12:00 EST is 09:00 PST (same day)
  return new Date(year, month - 1, day, 12, 0, 0);
}
